import React, { useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

/**
 * HOC wrapper to provide router hooks to class components
 * This replaces the deprecated withRouter from react-router v5
 *
 * Usage:
 * import { withRouterHooks } from '../helpers/withRouterHooks';
 * export default withRouterHooks(YourComponent);
 *
 * The component will receive these props:
 * - history: { push, replace, goBack, go }
 * - location: current location object
 * - match: { params } - route parameters
 * - navigate: direct access to navigate function
 * - params: direct access to params object
 */
export function withRouterHooks(Component) {
	return function WrappedComponent(props) {
		// Get router hooks
		const navigate = useNavigate();
		const location = useLocation();
		const params = useParams();

		// Keep navigate in a ref so the history object can be created once and
		// remain stable across renders. Without this, every render creates a new
		// history object, causing useCallback/useMemo deps that include `history`
		// to invalidate on every render — triggering spurious API calls.
		const navigateRef = useRef(navigate);
		navigateRef.current = navigate;

		const historyRef = useRef({
			push: (path, state) => navigateRef.current(path, { state }),
			replace: (path, state) => navigateRef.current(path, { replace: true, state }),
			goBack: () => navigateRef.current(-1),
			go: (delta) => navigateRef.current(delta),
			block: () => {
				console.warn(
					"history.block is not supported in React Router v6. Use useBlocker hook instead."
				);
				return () => {};
			},
		});

		// Keep location on history in sync (v5 back-compat) without rebuilding the object.
		historyRef.current.location = location;

		const history = historyRef.current;

		// Create match object that mimics react-router v5 API
		const match = {
			params,
			isExact: true, // This would need more complex logic to determine exactly
			path: location.pathname,
			url: location.pathname,
		};

		// Create combined props object with both original props and router props
		const combinedProps = {
			...props,
			history,
			location,
			match,
			navigate,
			params,
		};

		return <Component {...combinedProps} />;
	};
}

export default withRouterHooks;
